import {
  Add,
  ArrowBack,
  Close,
  ExpandMore,
  FilterList,
  Notifications,
  Stop,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BackdropLoading from "components/BackdropLoading";
import {
  DashboardStackCustom,
  StackCustom,
} from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "shared/hooks/auth";
import {
  getContadorNotificacoes,
  getNotificacoes,
  getQuadroKanban,
  getReferenciasQuadro,
  getResponsaveisQuadro,
  getStatusFiltro,
  getTagsFiltro,
  getTimerAtivo,
  IContadorNotificacoes,
  IFiltroQuadroTarefas,
  INotificacaoResumo,
  iniciarTimer,
  IQuadroKanban,
  IResponsavelFiltro,
  IStatusFiltro,
  ITagFiltro,
  ITimerAtivo,
  marcarNotificacaoLida,
  marcarTodasNotificacoesLidas,
  moverTarefa,
  pararTimer,
  STATUS_CONCLUIDO,
} from "services/modules/quadroTarefas";
import { IReferenciaOption } from "services/modules/tarefas";
import { KanbanColumn } from "./components/KanbanColumn";
import { ModalTarefaDetalhe } from "./components/ModalTarefaDetalhe";

/**
 * Página do Quadro de Tarefas (Kanban) - Fase 4 Etapa 2
 */
export const QuadroTarefas = () => {
  const { user, havePermission } = useAuth();
  const { openNotification } = useNavigation();

  // Sub-permissões de Tarefas (Fase 4 - Tarefa 1.4.5)
  // Regra de compatibilidade: admins passam sempre; quando o token NÃO possui o
  // marcador de esquema (token legado/empresa sem o recurso), libera tudo. Estando
  // sob o esquema, exige a sub-permissão específica.
  const ehAdminTarefas = havePermission(["Master"]); // FASE 04 item 2: "Configuracao" e a role de acesso a tela, nao um perfil administrativo (PO 20/08, F04-3b)
  const estaSobEsquema = havePermission(["Tarefa.Esquema"]);
  const podeCriarTarefa =
    ehAdminTarefas ||
    !estaSobEsquema ||
    havePermission(["Tarefa.CriarEditarMinhas"]);

  // Estados de carregamento
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Dados do quadro
  const [quadro, setQuadro] = useState<IQuadroKanban | null>(null);
  const [timerAtivo, setTimerAtivo] = useState<ITimerAtivo | null>(null);
  const [contadorNotificacoes, setContadorNotificacoes] =
    useState<IContadorNotificacoes>({ naoLidas: 0 });

  // Filtros e agrupamento
  const [filtroAberto, setFiltroAberto] = useState(false);
  const [filtros, setFiltros] = useState<Partial<IFiltroQuadroTarefas>>({});
  const [agrupamento, setAgrupamento] = useState<"STATUS" | "RESPONSÁVEL">("STATUS");

  // Dados de referência para filtros
  const [referencias, setReferencias] = useState<IReferenciaOption[]>([]);
  const [responsaveis, setResponsaveis] = useState<IResponsavelFiltro[]>([]);
  const [statusList, setStatusList] = useState<IStatusFiltro[]>([]);
  const [tags, setTags] = useState<ITagFiltro[]>([]);

  // Modal de parar timer
  const [modalPararTimer, setModalPararTimer] = useState(false);
  const [observacaoTimer, setObservacaoTimer] = useState("");

  // Notificações
  const [notificacoesAberto, setNotificacoesAberto] = useState(false);
  const [notificacoes, setNotificacoes] = useState<INotificacaoResumo[]>([]);
  const [carregandoNotificacoes, setCarregandoNotificacoes] = useState(false);

  // Modal de edição de tarefa
  const [modalTarefaAberto, setModalTarefaAberto] = useState(false);
  const [tarefaSelecionadaId, setTarefaSelecionadaId] = useState<number | null>(null);

  // Empresa do usuário
  const idEmpresa = user?.idEmpresa || 0;

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  const carregarQuadro = useCallback(async () => {
    if (!idEmpresa) return;

    setIsLoading(true);
    try {
      const filtroCompleto: IFiltroQuadroTarefas = {
        idEmpresa,
        ...filtros,
      };

      const [quadroData, timerData, notifData] = await Promise.all([
        getQuadroKanban(filtroCompleto),
        getTimerAtivo(),
        getContadorNotificacoes(),
      ]);

      setQuadro(quadroData);
      setTimerAtivo(timerData);
      setContadorNotificacoes(notifData);
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao carregar quadro de tarefas",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [idEmpresa, filtros, openNotification]);

  const carregarDadosIniciais = useCallback(async () => {
    if (!idEmpresa) return;
    
    // Os serviços getReferenciasQuadro/getResponsaveisQuadro agora propagam o erro
    // (não mascaram 500 como []); tratamos por-filtro aqui para a UI seguir
    // funcional sem esconder a falha (que já é logada/notificada no serviço).
    const [refs, resp, status, tagsList] = await Promise.all([
      getReferenciasQuadro(idEmpresa).catch(() => []),
      getResponsaveisQuadro(idEmpresa).catch(() => []),
      getStatusFiltro(),
      getTagsFiltro(idEmpresa),
    ]);
    setReferencias(refs);
    setResponsaveis(resp);
    setStatusList(status);
    setTags(tagsList);
  }, [idEmpresa]);

  useEffect(() => {
    carregarDadosIniciais();
  }, [carregarDadosIniciais]);

  useEffect(() => {
    carregarQuadro();
  }, [carregarQuadro]);

  // Polling para atualizar contador de notificações a cada 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      const notifData = await getContadorNotificacoes();
      setContadorNotificacoes(notifData);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // ============================================
  // HANDLERS
  // ============================================

  const handleMoverTarefa = async (
    idTarefa: number,
    idStatusNovo: number
  ) => {
    // 4.1.3 — Bloquear conclusão com subtarefas pendentes ANTES do update otimista.
    // (O backend também valida — HTTP 400 com { Mensagens: [...] } — e a mensagem
    // real é exibida no catch abaixo caso algo passe pelo gate do front.)
    if (idStatusNovo === STATUS_CONCLUIDO) {
      const tarefaAtual = quadro?.tarefas.find((t) => t.idTarefa === idTarefa);
      if (
        tarefaAtual &&
        tarefaAtual.quantidadeSubtarefas !==
          tarefaAtual.quantidadeSubtarefasConcluidas
      ) {
        openNotification({
          isOpen: true,
          message: "Conclua as subtarefas antes de concluir a tarefa.",
          type: "warning",
        });
        return;
      }
    }

    // Guardar estado anterior para reverter em caso de falha
    const quadroAnterior = quadro;

    // Atualização otimista: mover o card imediatamente para a coluna destino
    setQuadro((prev) =>
      prev
        ? {
            ...prev,
            tarefas: prev.tarefas.map((t) =>
              t.idTarefa === idTarefa
                ? { ...t, idTarefaStatus: idStatusNovo }
                : t
            ),
          }
        : prev
    );

    setIsProcessing(true);
    try {
      const ok = await moverTarefa({ idTarefa, idTarefaStatusNovo: idStatusNovo });
      if (!ok) {
        throw new Error("Não foi possível mover a tarefa");
      }
      await carregarQuadro();
      openNotification({
        isOpen: true,
        message: "Tarefa movida com sucesso!",
        type: "success",
      });
    } catch (error: any) {
      // Reverter a atualização otimista
      setQuadro(quadroAnterior);
      openNotification({
        isOpen: true,
        message: error?.message || "Erro ao mover tarefa",
        type: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClickCard = (idTarefa: number) => {
    setTarefaSelecionadaId(idTarefa);
    setModalTarefaAberto(true);
  };

  const handleCloseModalTarefa = () => {
    setModalTarefaAberto(false);
    setTarefaSelecionadaId(null);
  };

  const handleSaveTarefa = async () => {
    await carregarQuadro();
  };

  // 3.1.2 — aceita idSubtarefa opcional: quando informado, cronometra a subtarefa;
  // caso contrário, cronometra a tarefa (comportamento anterior).
  const handlePlayTimer = async (idTarefa?: number, idSubtarefa?: number) => {
    try {
      await iniciarTimer(
        idSubtarefa ? { idSubtarefa } : { idTarefa }
      );
      await carregarQuadro();
      openNotification({
        isOpen: true,
        message: "Timer iniciado!",
        type: "success",
      });
    } catch (error: any) {
      // 8.1.3 — exibir a mensagem real do backend (ex.: 400 de permissão).
      openNotification({
        isOpen: true,
        message: error?.message || "Erro ao iniciar timer",
        type: "error",
      });
    }
  };

  const handleStopTimer = async () => {
    if (!timerAtivo) return;

    try {
      await pararTimer({
        idTarefaTimer: timerAtivo.idTarefaTimer,
        observacao: observacaoTimer,
      });
      setModalPararTimer(false);
      setObservacaoTimer("");
      await carregarQuadro();
      openNotification({
        isOpen: true,
        message: "Timer parado e horas registradas!",
        type: "success",
      });
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao parar timer",
        type: "error",
      });
    }
  };

  const handleAplicarFiltros = () => {
    setFiltroAberto(false);
    carregarQuadro();
  };

  const handleLimparFiltros = () => {
    setFiltros({});
    setFiltroAberto(false);
  };

  // ============================================
  // HANDLERS - NOTIFICAÇÕES
  // ============================================

  const handleAbrirNotificacoes = async () => {
    setNotificacoesAberto(true);
    setCarregandoNotificacoes(true);
    try {
      const notifs = await getNotificacoes();
      setNotificacoes(notifs);
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao carregar notificações",
        type: "error",
      });
    } finally {
      setCarregandoNotificacoes(false);
    }
  };

  const handleLimparNotificacoes = async () => {
    try {
      await marcarTodasNotificacoesLidas();
      setNotificacoes([]);
      setContadorNotificacoes({ naoLidas: 0 });
      openNotification({
        isOpen: true,
        message: "Todas as notificações foram marcadas como lidas",
        type: "success",
      });
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao limpar notificações",
        type: "error",
      });
    }
  };

  const handleClickNotificacao = async (notif: INotificacaoResumo) => {
    setNotificacoesAberto(false);

    if (notif.idTarefa) {
      setTarefaSelecionadaId(notif.idTarefa);
      setModalTarefaAberto(true);
    }

    // Marca a notificação como lida ao visualizá-la (se ainda não estiver)
    if (!notif.lida) {
      try {
        await marcarNotificacaoLida(notif.idTarefaNotificacao);
        setNotificacoes((prev) =>
          prev.map((n) =>
            n.idTarefaNotificacao === notif.idTarefaNotificacao
              ? { ...n, lida: true }
              : n
          )
        );
        setContadorNotificacoes((prev) => ({
          naoLidas: Math.max(0, prev.naoLidas - 1),
        }));
      } catch (error) {
        // Falha ao marcar como lida não deve impedir a abertura da tarefa
      }
    }
  };

  // Função para formatar o número da tarefa com zeros à esquerda
  const formatarNumeroTarefa = (numero: number | null): string => {
    if (!numero) return "";
    return numero.toString().padStart(8, "0");
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <BackdropLoading isOpen={isProcessing} />

      <DashboardStackCustom>
        {/* Cabeçalho com título e botões */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          {/* Lado esquerdo: Título */}
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              GERENCIAMENTO DE SOLICITAÇÕES
            </Typography>
          </Box>

          {/* Lado direito: Botões */}
          <Box display="flex" alignItems="center" gap={1}>
            {/* Timer Ativo */}
            {timerAtivo && (
              <Chip
                icon={<Stop />}
                label={`Timer: ${timerAtivo.tituloTarefa || timerAtivo.nomeSubtarefa}`}
                color="success"
                onClick={() => setModalPararTimer(true)}
                sx={{ cursor: "pointer" }}
              />
            )}

            {/* Notificações */}
            <Tooltip title="Notificações">
              <IconButton onClick={handleAbrirNotificacoes}>
                <Badge
                  badgeContent={contadorNotificacoes.naoLidas}
                  color="error"
                >
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Nova Tarefa - exige sub-permissão "Criar e editar minhas tarefas" (admins passam) */}
            {podeCriarTarefa && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => {
                  setTarefaSelecionadaId(null);
                  setModalTarefaAberto(true);
                }}
              >
                ADICIONAR TAREFA
              </Button>
            )}
          </Box>
        </Box>

        {/* Breadcrumb */}
        <Typography variant="body2" color="textSecondary" mb={1}>
          Home &gt;
        </Typography>

        {/* Linha de separação azul */}
        <Divider sx={{ borderColor: "primary.main", borderWidth: 2, mb: 2 }} />

        {/* Barra de Agrupamento e Filtros */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mb={2}
        >
          {/* Agrupar por */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="textSecondary">
              Agrupar por:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={agrupamento}
                onChange={(e) => setAgrupamento(e.target.value as "STATUS" | "RESPONSÁVEL")}
                sx={{
                  "& .MuiSelect-select": {
                    py: 0.5,
                    fontWeight: "bold",
                    color: "primary.main",
                  },
                }}
              >
                <MenuItem value="STATUS">STATUS</MenuItem>
                <MenuItem value="RESPONSÁVEL">RESPONSÁVEL</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Filtros */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }}
            onClick={() => setFiltroAberto(true)}
          >
            Filtros
            <FilterList fontSize="small" />
          </Typography>
        </Box>

        {/* Quadro Kanban */}
        <StackCustom sx={{ flexGrow: 1 }}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
            >
              <CircularProgress />
            </Box>
          ) : quadro ? (
            <Box
              display="flex"
              gap={2}
              sx={{
                overflowX: "auto",
                pb: 2,
                "&::-webkit-scrollbar": {
                  height: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: 4,
                },
              }}
            >
              {quadro.colunas.map((coluna) => (
                <KanbanColumn
                  key={coluna.idTarefaStatus}
                  coluna={coluna}
                  tarefas={quadro.tarefas}
                  onClickCard={handleClickCard}
                  onPlayTimer={handlePlayTimer}
                  onStopTimer={() => setModalPararTimer(true)}
                  timerAtivoIdTarefa={timerAtivo?.idTarefa}
                  onDropTarefa={handleMoverTarefa}
                />
              ))}
            </Box>
          ) : (
            <Box textAlign="center" py={8}>
              <Typography color="textSecondary">
                Nenhum quadro de tarefas disponível
              </Typography>
            </Box>
          )}
        </StackCustom>
      </DashboardStackCustom>

      {/* Drawer de Filtros */}
      <Drawer
        anchor="right"
        open={filtroAberto}
        onClose={() => setFiltroAberto(false)}
      >
        <Box sx={{ width: 320, display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Cabeçalho */}
          <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setFiltroAberto(false)}
              sx={{ textTransform: "none" }}
            >
              VOLTAR
            </Button>
            <IconButton onClick={() => setFiltroAberto(false)} size="small">
              <Close />
            </IconButton>
          </Box>

          {/* Título */}
          <Typography variant="h6" align="center" sx={{ mb: 2 }}>
            FILTROS
          </Typography>

          {/* Accordions de Filtros */}
          <Box sx={{ flex: 1, overflow: "auto", px: 1 }}>
            {/* Responsáveis */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography color="primary" fontWeight="medium">
                  RESPONSÁVEIS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                  {responsaveis.length === 0 ? (
                    <ListItem>
                      <ListItemText 
                        primary="Nenhum responsável disponível" 
                        primaryTypographyProps={{ color: "textSecondary", fontSize: "0.875rem" }}
                      />
                    </ListItem>
                  ) : (
                    responsaveis.map((resp) => (
                      <ListItem key={resp.idUsuario} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() => {
                            setFiltros((prev) => {
                              const current = prev.idResponsaveis || [];
                              const isSelected = current.includes(resp.idUsuario);
                              return {
                                ...prev,
                                idResponsaveis: isSelected
                                  ? current.filter((id) => id !== resp.idUsuario)
                                  : [...current, resp.idUsuario],
                              };
                            });
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Checkbox
                              edge="start"
                              checked={(filtros.idResponsaveis || []).includes(resp.idUsuario)}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText primary={resp.nome} />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Referências */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography color="primary" fontWeight="medium">
                  REFERÊNCIAS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                  {referencias.length === 0 ? (
                    <ListItem>
                      <ListItemText 
                        primary="Nenhuma referência disponível" 
                        primaryTypographyProps={{ color: "textSecondary", fontSize: "0.875rem" }}
                      />
                    </ListItem>
                  ) : (
                    referencias.map((ref) => (
                      <ListItem key={ref.value} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() => {
                            setFiltros((prev) => {
                              const current = prev.idReferencias || [];
                              const isSelected = current.includes(ref.value);
                              return {
                                ...prev,
                                idReferencias: isSelected
                                  ? current.filter((id) => id !== ref.value)
                                  : [...current, ref.value],
                              };
                            });
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Checkbox
                              edge="start"
                              checked={(filtros.idReferencias || []).includes(ref.value)}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText primary={ref.label} />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Status */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography color="primary" fontWeight="medium">
                  STATUS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense>
                  {statusList.map((status) => (
                    <ListItem key={status.idTarefaStatus} disablePadding>
                      <ListItemButton
                        dense
                        onClick={() => {
                          setFiltros((prev) => {
                            const current = prev.idStatus || [];
                            const isSelected = current.includes(status.idTarefaStatus);
                            return {
                              ...prev,
                              idStatus: isSelected
                                ? current.filter((id) => id !== status.idTarefaStatus)
                                : [...current, status.idTarefaStatus],
                            };
                          });
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox
                            edge="start"
                            checked={(filtros.idStatus || []).includes(status.idTarefaStatus)}
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText primary={status.nome} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Tags */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography color="primary" fontWeight="medium">
                  TAGS
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                  {tags.length === 0 ? (
                    <ListItem>
                      <ListItemText 
                        primary="Nenhuma tag disponível" 
                        primaryTypographyProps={{ color: "textSecondary", fontSize: "0.875rem" }}
                      />
                    </ListItem>
                  ) : (
                    tags.map((tag) => (
                      <ListItem key={tag.tag} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() => {
                            setFiltros((prev) => {
                              const current = prev.tags || [];
                              const isSelected = current.includes(tag.tag);
                              return {
                                ...prev,
                                tags: isSelected
                                  ? current.filter((t) => t !== tag.tag)
                                  : [...current, tag.tag],
                              };
                            });
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Checkbox
                              edge="start"
                              checked={(filtros.tags || []).includes(tag.tag)}
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText primary={tag.tag} />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Botões */}
          <Box sx={{ p: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={handleLimparFiltros}>LIMPAR</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAplicarFiltros}
            >
              APLICAR
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Drawer de Notificações */}
      <Drawer
        anchor="right"
        open={notificacoesAberto}
        onClose={() => setNotificacoesAberto(false)}
      >
        <Box sx={{ width: 350, display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Cabeçalho */}
          <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setNotificacoesAberto(false)}
              sx={{ textTransform: "none" }}
            >
              VOLTAR
            </Button>
            <IconButton onClick={() => setNotificacoesAberto(false)} size="small">
              <Close />
            </IconButton>
          </Box>

          {/* Título e Limpar */}
          <Box sx={{ px: 2, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" color="primary" fontWeight="medium">
              NOTIFICAÇÕES
            </Typography>
            <Button
              size="small"
              onClick={handleLimparNotificacoes}
              disabled={notificacoes.length === 0}
              sx={{ textTransform: "none" }}
            >
              LIMPAR
            </Button>
          </Box>

          <Divider />

          {/* Lista de Notificações */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {carregandoNotificacoes ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            ) : notificacoes.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="textSecondary">
                  Nenhuma notificação
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {notificacoes.map((notif) => (
                  <ListItem
                    key={notif.idTarefaNotificacao}
                    disablePadding
                    sx={{
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: notif.lida ? "transparent" : "action.hover",
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleClickNotificacao(notif)}
                      sx={{ py: 1.5 }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "primary.main",
                            fontSize: "0.875rem",
                          }}
                        >
                          {notif.tipoNotificacao?.charAt(0) || "N"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box>
                            {notif.numeroTarefa && (
                              <Link
                                component="span"
                                sx={{
                                  color: "primary.main",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {formatarNumeroTarefa(notif.numeroTarefa)}
                              </Link>
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {notif.mensagem || notif.titulo}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Modal Parar Timer */}
      <Dialog
        open={modalPararTimer}
        onClose={() => setModalPararTimer(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Parar Timer</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Deseja parar o timer da tarefa{" "}
            <strong>{timerAtivo?.tituloTarefa || timerAtivo?.nomeSubtarefa}</strong>?
          </Typography>
          <TextField
            label="Observação (opcional)"
            fullWidth
            multiline
            rows={3}
            value={observacaoTimer}
            onChange={(e) => setObservacaoTimer(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalPararTimer(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleStopTimer}>
            Parar Timer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição de Tarefa - Etapa 3 */}
      <ModalTarefaDetalhe
        open={modalTarefaAberto}
        idTarefa={tarefaSelecionadaId}
        onClose={handleCloseModalTarefa}
        onSave={handleSaveTarefa}
      />
    </>
  );
};

